// Update designs: the run record, the left menu indicators and the file state
// chip, as pure functions of what `GET …/attention/` returns.
// docs/update-designs-proposal.md §5.5–§5.7.

export const RUN_ACTIVE_STATES = ["queued", "running"]
export const RUN_FINISHED_STATES = ["done", "failed", "stopped"]
export const STAGES = ["split", "designs", "details"]

// Sections a run reads, so the row menu offers "Update designs from this file"
// only on the files the pipeline uses.
export const PIPELINE_SECTIONS = [
    "project_ifc", "module_ifc", "external_ifc",
    "assembly_ifc", "assembly_btlx", "extra_assembly_ifc", "assembly_drawing", "assembly_alt_model",
    "extra_module_ifc", "module_drawing",
    "detail", "spec",
]

/** Queued or running, whether or not it still answers. */
export function runIsActive(run) {
    return !!run && RUN_ACTIVE_STATES.includes(run.state)
}

/** Queued or running and still answering: what locks the matrix and spins. */
export function runIsLive(run) {
    return runIsActive(run) && !run.stalled
}

/** A finished run whose banner has not been closed yet. */
export function runNeedsBanner(run) {
    if (!run) return false
    if (runIsActive(run)) return true
    return RUN_FINISHED_STATES.includes(run.state) && !run.dismissed
}

/**
 * Whether the quantities matrix is locked: from the moment the designs stage
 * starts until the run ends. That stage creates revisions and moves units and
 * counts to them; the split before it touches neither the designs nor the
 * plan, so the matrix stays open while a big file is being split.
 */
export function runLocksMatrix(run) {
    return runIsLive(run) && ["running", "done", "failed"].includes(run?.stages?.designs?.state)
}

/** The run's progress over its stages that do work, 0..100. */
export function runPerc(run) {
    if (!run?.stages) return 0
    const working = STAGES.filter(s => run.stages[s] && run.stages[s].state != "skipped")
    if (!working.length) return 0
    const sum = working.reduce((a, s) => {
        const st = run.stages[s]
        if (st.state == "done" || st.state == "failed") return a + 100
        return a + Math.min(100, Math.max(0, Number(st.perc) || 0))
    }, 0)
    return Math.round(sum / working.length)
}

export function pendingCount(attention) {
    const files = attention?.files
    if (!files) return 0
    if (typeof files.count == "number") return files.count
    return Object.keys(files.pending || {}).length
}

/** Files left in Auto-detect with nothing recognised: they wait for someone
 * to set their category, and Update designs never acts on them. */
export function needsCategoryCount(attention) {
    return Number(attention?.files?.needs_category?.count) || 0
}

/**
 * What the Files menu entry shows.
 * { kind: "none" | "pill" | "spinner" | "alert", text, color }
 * The pill counts the files waiting for Update designs plus the ones waiting
 * for a category: both are work left on the Files page.
 */
export function filesIndicator(attention) {
    const run = attention?.run
    if (runIsLive(run)) return { kind: "spinner", color: "accent" }
    if (run && !run.dismissed && (run.state == "failed" || (runIsActive(run) && run.stalled)))
        return { kind: "alert", color: "error" }
    const pending = pendingCount(attention)
    const needs_category = needsCategoryCount(attention)
    const n = pending + needs_category
    if (n > 0) return { kind: "pill", text: String(n), color: "accent", count: n, pending, needs_category }
    return { kind: "none" }
}

/**
 * What the Production counts menu entry shows. `quantities` is the server's
 * drift, or the matrix's own while it is open.
 */
export function countsIndicator(attention, quantities = attention?.quantities) {
    if (runLocksMatrix(attention?.run)) return { kind: "lock", color: "grey" }
    if (!quantities) return { kind: "none" }
    const to_create = quantities.to_create || 0
    const to_delete = quantities.to_delete || 0
    if (to_create || to_delete) {
        const parts = []
        if (to_create) parts.push(`+${to_create}`)
        // Minus sign, not a hyphen: it is a number.
        if (to_delete) parts.push(`−${to_delete}`)
        return { kind: "pill", text: parts.join(" "), color: "accent", to_create, to_delete }
    }
    if (quantities.stale) return { kind: "dot", color: "accent" }
    return { kind: "none" }
}

/** Whether either entry has something to say: the phone menu toggle's dot. */
export function anyIndicator(attention, quantities) {
    return filesIndicator(attention).kind != "none" || !["none", "lock"].includes(countsIndicator(attention, quantities).kind)
}

function dname(file) {
    return file?.meta?.design_name || file?.short_name || (file?.name || "").replace(/\.[^.]+$/, "")
}

/**
 * The one chip of a file in the Status column, or null.
 * { key, tone: "running" | "error" | "pending" | "done", text_key, params, perc }
 * `reason` is the file's entry in attention.files.pending; `split_units` the
 * units a split source counts, when known.
 */
export function fileChip(file, reason, { split_units = null } = {}) {
    const status = file?.meta?.status || []
    // A file left in Auto-detect is in no pipeline section until it has a
    // category (docs/timber-panel-tipos-proposal.md §9.3).
    if (file?.meta?.section == "auto") {
        const state = file.meta.auto?.state
        if (state == "reading") return { key: "reading_ifc", tone: "running", text_key: "file_state_reading_ifc" }
        if (state == "waiting_design")
            return { key: "waiting_design", tone: "pending", text_key: "file_state_waiting_design", params: { name: file.meta.auto?.design_name || dname(file) } }
        return { key: "needs_category", tone: "pending", text_key: "file_state_needs_category" }
    }
    if (status.includes("splitting")) {
        const perc = file.meta?.split_perc
        return { key: "splitting", tone: "running", text_key: "file_state_splitting", perc: perc ?? null }
    }
    if (status.includes("split_failed"))
        return { key: "split_failed", tone: "error", text_key: "file_state_split_failed" }
    if (status.includes("busy"))
        return { key: "waiting", tone: "running", text_key: "file_state_waiting" }
    if (reason == "split") return { key: "to_split", tone: "pending", text_key: "file_state_to_split" }
    if (reason == "designs") return { key: "not_in_design", tone: "pending", text_key: "file_state_not_in_design" }
    if (reason == "scan") return { key: "not_scanned", tone: "pending", text_key: "file_state_not_scanned" }
    if (status.includes("splitted"))
        return { key: "split", tone: "done", text_key: split_units != null ? "file_state_split_units" : "file_state_split", params: { n: split_units } }
    if (status.includes("designs"))
        return { key: "in_design", tone: "done", text_key: "file_state_in_design", params: { name: dname(file) } }
    if (status.includes("scanned")) return { key: "scanned", tone: "done", text_key: "file_state_scanned" }
    return null
}

/** Busy files cannot be deleted: a run or a split is working on them. */
export function fileIsBusy(file) {
    const status = file?.meta?.status || []
    return status.includes("busy") || status.includes("splitting")
}

/** The split files the dialog shows: every candidate the server lists. A
 * module IFC uploaded as such is one, like a project IFC
 * (woodwall/companies/projects/design_update.py, is_split_source). */
export function visibleSplitFiles(preview, options, key = "files") {
    return preview?.split?.[key] || []
}

/**
 * Which stages have pending work, given the preview and the options in the
 * dialog. The designs stage has work when the split stage runs: the split
 * makes the files it reads.
 */
export function stageWork(preview, options) {
    const split = !!preview?.splitter && visibleSplitFiles(preview, options).length > 0
    const d = preview?.designs || {}
    const split_on = !!preview?.splitter && !!options?.split?.enabled &&
        (split || (!!options?.split?.all && stageDone(preview, options).split > 0))
    const designs = split_on || (d.new?.length || 0) + (d.revised?.length || 0) + (d.files || 0) > 0
    const det = preview?.details || {}
    const details = (det.drawings || 0) + (det.details || 0) > 0 || !!options?.details?.ifc
    return { split, designs, details }
}

/**
 * What each stage already did, which it can do again (`all`). Pending is only
 * what the file tags say: a new parsing setup, for one, needs the building
 * IFCs split again, and nothing can tell that from the tags. So the dialog
 * never says a stage has nothing to do; it says what is pending and what is
 * done, and lets the user take the done files too.
 */
export function stageDone(preview, options) {
    const det = preview?.details || {}
    return {
        split: visibleSplitFiles(preview, options, "done_files").length,
        designs: preview?.designs?.done_files || 0,
        details: (det.done_drawings || 0) + (det.done_details || 0),
    }
}

/** The options sent to the backend: a stage with nothing to take is off. */
export function effectiveOptions(preview, options) {
    const work = stageWork(preview, options)
    const done = stageDone(preview, options)
    const o = JSON.parse(JSON.stringify(options || {}))
    for (const s of STAGES) {
        o[s] = { ...(o[s] || {}) }
        o[s].all = !!o[s].all && done[s] > 0
        o[s].enabled = !!o[s].enabled && (work[s] || o[s].all)
    }
    // The split needs the module, whatever the user asks.
    if (!preview?.splitter) o.split.enabled = false
    return o
}

/** Whether the dialog's button can launch the run. */
export function canLaunch(preview, options) {
    const o = effectiveOptions(preview, options)
    if (!STAGES.some(s => o[s].enabled)) return false
    if (o.details.enabled && !o.details.pdf && !o.details.ifc) return false
    return true
}
